import React from "react";
import { Button, Tag } from "@jem-open/jem-ui";
import { Download, Printer, X } from "lucide-react";
import { PreviewParams } from "./types";
import { fmtDate, shiftLabel } from "./previewHelpers";

interface PreviewHeaderProps {
  title: string;
  subtitle: string;
  params: PreviewParams;
  onDownload: () => void;
}

const PreviewHeader = ({
  title,
  subtitle,
  params,
  onDownload,
}: PreviewHeaderProps): JSX.Element => {
  let dateRange = "—";
  if (params.date) {
    dateRange = fmtDate(params.date);
  } else if (params.dateFrom && params.dateTo) {
    dateRange = `${fmtDate(params.dateFrom)} – ${fmtDate(params.dateTo)}`;
  }

  return (
    <>
      <div className="sticky top-0 z-10 bg-white border-b border-greyscale-border">
        <div className="max-w-[960px] mx-auto px-md sm:px-lg py-sm flex flex-wrap items-center justify-between gap-sm">
          <div className="flex items-center gap-xs">
            <Tag variant="drafted" size="sm">
              Preview
            </Tag>
            <p className="text-xs text-greyscale-text-caption">
              Live preview — data reflects current report settings.
            </p>
          </div>
          <div className="flex items-center gap-xs w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => window.print()}
              leftIcon={<Printer size={16} />}
              className="flex-1 sm:flex-none"
            >
              Print
            </Button>
            <Button
              onClick={onDownload}
              leftIcon={<Download size={16} />}
              className="flex-1 sm:flex-none"
            >
              Download
            </Button>
            <button
              type="button"
              onClick={() => window.close()}
              aria-label="Close preview"
              className="rounded-md p-xs text-greyscale-text-caption hover:bg-neutral-50"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="mb-lg">
        <div className="flex flex-wrap items-start justify-between gap-sm">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
              {subtitle}
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-greyscale-text-title mt-xxxs break-words">
              {title}
            </h1>
          </div>
          <div className="text-sm text-greyscale-text-body text-right">
            <p className="font-semibold text-greyscale-text-title">
              {params.scopeLabel}
            </p>
            {params.scopeRegion && (
              <p className="text-xs text-greyscale-text-caption">
                {params.scopeRegion}
              </p>
            )}
            {params.scopeParent && (
              <p className="text-xs text-greyscale-text-caption">
                in {params.scopeParent}
              </p>
            )}
          </div>
        </div>

        <div className="mt-sm grid grid-cols-2 sm:grid-cols-4 gap-sm text-xs border-t border-greyscale-border pt-sm">
          <div>
            <p className="uppercase tracking-wide text-greyscale-text-caption">
              Scope
            </p>
            <p className="font-semibold text-greyscale-text-title capitalize">
              {params.scopeKind}
            </p>
          </div>
          <div>
            <p className="uppercase tracking-wide text-greyscale-text-caption">
              Period
            </p>
            <p className="font-semibold text-greyscale-text-title">
              {dateRange}
            </p>
          </div>
          <div>
            <p className="uppercase tracking-wide text-greyscale-text-caption">
              Shift
            </p>
            <p className="font-semibold text-greyscale-text-title">
              {shiftLabel(params.shift)}
            </p>
          </div>
          <div>
            <p className="uppercase tracking-wide text-greyscale-text-caption">
              Generated
            </p>
            <p className="font-semibold text-greyscale-text-title">
              {fmtDate(new Date())}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewHeader;
